import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Share2, ArrowRight, Menu, Mic, MicOff, Link2, Info } from 'lucide-react';
import imgUrbanIcon from '../../../assets/PNG/urban_icon.png';
import * as S from './MainContent.styles';
import { SuggestionsPopup } from '../suggestionPopUp/SuggestionsPopup';
import { Loading } from '../../../components/uielements/loading/loading';
import {
  getCouncilInformation,
  getConversation,
  saveConversation,
  getAddressList,
} from '../../../services/publicChatBot';
import { useLanguage } from '../../../hooks/useLanguageSwitch';
import useDebounce from '../../../hooks/useDebounce';
import { formatBotMessage } from '../../../helper/aiRespondFormatter';
import AddressSuggestionsList from '../addressSuggestion/addressSuggestion';
import { EndConversationPopup } from '../EndConversationPopup/EndConversationPopup';
import { useAppSharedData } from '../../../hooks/useAppSharedData';

// Generate random session ID
const generateSessionId = () => {
  return Array.from({ length: 30 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
};

const CHARACTER_LIMIT = 400;

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export const MainContent = ({ onMenuClick }) => {
  const { id } = useParams();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [agentCode, setAgentCode] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [councilInfo, setCouncilInfo] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [showEndConversation, setShowEndConversation] = useState(false);
  const [isSavingConversation, setIsSavingConversation] = useState(false);

  // Initial flow states
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [initialFlowComplete, setInitialFlowComplete] = useState(false);
  const [collectedData, setCollectedData] = useState({
    username: '',
    propertyAddress: '',
  });

  // Address handling states
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [questionNumber, setQuestionNumber] = useState(2);
  const [userAddressInput, setUserAddressInput] = useState('');
  const [addressSelected, setAddressSelected] = useState(false);
  const [suggestionClick, setSuggestionClick] = useState(false);

  const { setData } = useAppSharedData();

  // Refs
  const recognitionRef = useRef(null);
  const lastMessageRef = useRef(null);
  const inputRef = useRef(null);
  const lang = useLanguage();

  const INITIAL_QUESTIONS =
    councilInfo?.preQuestionList?.filter((item) => item.question !== '') || [];

  const currentQuestion = INITIAL_QUESTIONS[currentQuestionIndex];

  // Check if any INPUT-API question exists at all
  const hasInputApiQuestion = INITIAL_QUESTIONS.some((q) => q.inputType === 'INPUT-API');

  // Whether the user has hit the character limit — drives the red warning
  // shown above the input, replacing the old always-visible helper text.
  const isAtCharacterLimit = message.length >= CHARACTER_LIMIT;

  // Address autocomplete hook
  const {
    setSearchTerm,
    results: addressSuggestions,
    isLoading: isLoadingAddresses,
    clearResults,
  } = useDebounce(getAddressList, true, 300);

  useEffect(() => {
    if (lang?.selectedLang) {
      setSelectedLanguage(lang.selectedLang);
    }
  }, [lang?.selectedLang]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      // Reset height to auto to get the correct scrollHeight
      inputRef.current.style.height = 'auto';
      // Set height based on scrollHeight, but max 96px (4 lines)
      const newHeight = Math.min(inputRef.current.scrollHeight, 96);
      inputRef.current.style.height = `${newHeight}px`;
    }
  }, [message]);

  // Initialize on component mount
  useEffect(() => {
    const initializeChatBot = async () => {
      if (!id) {
        console.error('No agent ID found in URL');
        setIsLoading(false);
        return;
      }

      setAgentCode(id);
      setSessionId(generateSessionId());

      try {
        const response = await getCouncilInformation({ agentCode: id });
        setCouncilInfo(response);
        setData({
          logoUrl: response?.iconLogoUrl,
          councilName: response?.councilName,
        });

        // Show initial question immediately
        if (response?.preQuestionList?.length > 0) {
          const firstQuestion = response.preQuestionList.filter((item) => item.question !== '')[0];
          if (firstQuestion) {
            setMessages([
              {
                role: 'BOT',
                message: firstQuestion.question,
                createdAt: new Date().toISOString(),
              },
            ]);
          }
        }
      } catch (error) {
        console.error('Error fetching council information:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeChatBot();
  }, [id]);

  // Auto-focus input field after loading
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  // Auto-focus input after sending message or receiving response
  useEffect(() => {
    if (!isSending && !isListening && inputRef.current) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isSending, messages, isListening]);

  // Initialize Speech Recognition
  useEffect(() => {
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = selectedLanguage;
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onresult = (event) => {
        const speechResult = event.results[0][0].transcript;
        setMessage((prev) => `${prev} ${speechResult}`.trim());
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.onend = () => setIsListening(false);
      recognitionRef.current = recognition;
    }
  }, [selectedLanguage]);

  // Auto-scroll to latest message
  useEffect(() => {
    const timer = setTimeout(() => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [messages, isSending]);

  // Handle address search during initial flow
  useEffect(() => {
    if (
      !initialFlowComplete &&
      currentQuestion?.inputType === 'INPUT-API' &&
      userAddressInput.length > 2 &&
      !addressSelected
    ) {
      setSearchTerm(userAddressInput);
      setShowAddressSuggestions(true);
    } else if (currentQuestionIndex !== 1 || addressSelected) {
      setShowAddressSuggestions(false);
      clearResults();
    }
  }, [userAddressInput, currentQuestionIndex, initialFlowComplete, addressSelected]);

  const handleStartListening = async () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }

    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
      if (permissionStatus.state === 'denied') {
        alert(
          'Microphone access is blocked. Please allow microphone permissions in your browser settings.'
        );
        return;
      }

      recognitionRef.current.lang = selectedLanguage;
      recognitionRef.current.start();
      setIsListening(true);
    } catch (error) {
      alert('Unable to access the microphone. Please check your browser settings.');
    }
  };

  const handleStopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const handleAddressSelect = (address) => {
    setUserAddressInput(address);
    setMessage(address);
    setAddressSelected(true);
    setShowAddressSuggestions(false);
    clearResults();
    // Focus input after address selection
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSendMessage = async (messageText = message) => {
    if (!messageText.trim()) return;

    // Add user message
    const userMsg = {
      role: 'USER',
      message: messageText,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);

    // If we're in initial flow, save the data
    if (!initialFlowComplete && currentQuestionIndex < INITIAL_QUESTIONS.length) {
      const currentQuestion = INITIAL_QUESTIONS[currentQuestionIndex];

      if (currentQuestion.saveFlag === 1 && currentQuestion.payloadKey) {
        setCollectedData((prev) => ({
          ...prev,
          [currentQuestion.payloadKey]: messageText,
        }));
      }
    }

    setMessage('');
    setUserAddressInput('');
    setAddressSelected(false);
    setShowAddressSuggestions(false);
    clearResults();
    setIsSending(true);

    try {
      // Build payload
      const payload = {
        question: messageText,
        sessionId: sessionId,
        agentCode: agentCode,
        questionNumber: questionNumber,
      };

      // Add collected data if available
      if (collectedData.username) {
        payload.username = collectedData.username;
      }
      if (collectedData.propertyAddress) {
        payload.propertyAddress = collectedData.propertyAddress;
      }

      const response = await getConversation(payload);

      // Extract bot response
      let botResponse = 'No response from bot.';
      if (response) {
        if (typeof response === 'string') {
          botResponse = response;
        } else if (response.output) {
          botResponse = response.output;
        } else if (response.data && response.data.output) {
          botResponse = response.data.output;
        } else if (response.message) {
          botResponse = response.message;
        } else if (response.answer) {
          botResponse = response.answer;
        }
      }

      const botMsg = {
        role: 'BOT',
        message: botResponse,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, botMsg]);

      // Increment question number
      setQuestionNumber((prev) => prev + 1);

      // Handle initial flow progression
      if (!initialFlowComplete && currentQuestionIndex < INITIAL_QUESTIONS.length - 1) {
        // Move to next question
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);
      } else if (!initialFlowComplete && currentQuestionIndex === INITIAL_QUESTIONS.length - 1) {
        // Initial flow complete, show suggestions
        setInitialFlowComplete(true);
      }
    } catch (err) {
      console.error('Error fetching response:', err);
      setMessages((prev) => [
        ...prev,
        {
          role: 'BOT',
          message: 'Error fetching response. Please try again.',
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    // Send message on Enter (without Shift)
    // Allow new line on Shift+Enter
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    if (e && e.target) {
      const value = e.target.value;
      setMessage(value);
      // Update userAddressInput only during initial flow address question
      if (!initialFlowComplete && currentQuestion?.inputType === 'INPUT-API') {
        setUserAddressInput(value);
        if (addressSelected) {
          setAddressSelected(false);
        }
      }
    }
  };

  const handleSuggestionClick = (question) => {
    setMessage(question);
    handleSendMessage(question);
    setSuggestionClick(true);
  };

  const handleEndConversation = () => {
    setShowEndConversation(true);
  };

  const handleStartDA = () => {
    const portalLink = councilInfo?.portalLink;
    window.open(portalLink, '_blank');
  };

  const handleSaveConversation = async (email) => {
    setIsSavingConversation(true);
    try {
      await saveConversation({
        email: email,
        chat: messages,
        chatbotId: councilInfo?._id,
      });

      // Reset everything
      setMessages([]);
      setShowEndConversation(false);
      setSessionId(generateSessionId());
      setQuestionNumber(1);
      setCurrentQuestionIndex(0);
      setInitialFlowComplete(false);
      setCollectedData({
        username: '',
        propertyAddress: '',
      });
      setMessage('');
      setUserAddressInput('');
      setAddressSelected(false);
      setSuggestionClick(false);

      alert('Email successfully sent. Please review your inbox for the conversation report.');
      window.location.reload();

      // Focus input after reset
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (error) {
      console.error('Error saving conversation:', error);
      alert('Failed to send the email. Please try again.');
    } finally {
      setIsSavingConversation(false);
    }
  };

  const handleCancelEnd = () => {
    setShowEndConversation(false);
    // Focus input after closing popup
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Get current placeholder text — the character limit is now communicated
  // through the default placeholder instead of a permanent helper line.
  const getCurrentPlaceholder = () => {
    if (!initialFlowComplete) {
      if (currentQuestionIndex === 0) return 'Type your name here...';
      if (currentQuestionIndex === 1) return 'Type your property address...';
    }
    return `Message Urban Compass... (max ${CHARACTER_LIMIT} characters)`;
  };

  if (isLoading) {
    return (
      <S.ContentContainer>
        <Loading />
      </S.ContentContainer>
    );
  }

  return (
    <S.ContentContainer>
      <S.MobileMenuButton onClick={onMenuClick}>
        <Menu />
      </S.MobileMenuButton>

      {messages.length > 3 && (
        <S.ShareButton onClick={handleEndConversation} offset={Boolean(councilInfo?.portalLink)}>
          <Share2 />
          <span>Share</span>
        </S.ShareButton>
      )}

      {councilInfo?.portalLink && (
        <S.StartDAButton onClick={handleStartDA}>
          <Link2 />
          <span>Start DA</span>
        </S.StartDAButton>
      )}

      <S.MessagesScrollContainer>
        <S.HeaderSection>
          <S.HeaderContent>
            <S.LogoCircle>
              <img src={imgUrbanIcon} alt="Urban Compass" style={{ marginLeft: '-5px' }} />
            </S.LogoCircle>

            <S.Title>{councilInfo?.introData?.title || 'How can I help you today?'}</S.Title>
          </S.HeaderContent>

          <S.Description>
            {councilInfo?.introData?.intro ? (
              <span>{councilInfo?.introData?.intro}</span>
            ) : (
              <>
                <span>Hi, I&apos;m </span>
                <span className="bold">URBAN COMPASS</span>
                <span>
                  . Ask me anything about development or your property. Click Start DA to access
                  Urban Compass&apos;s full suite of AI-powered tools — make enquiries, generate
                  pre-lodgement reports, and when ready, easily create or convert your pre-lodgement
                  into a final application.
                </span>
              </>
            )}
          </S.Description>
        </S.HeaderSection>

        {/* Messages Display */}
        {messages.length > 0 && (
          <>
            {messages.map((msg, index) => (
              <S.MessageWrapper
                key={index}
                ref={index === messages.length - 1 ? lastMessageRef : null}
                $isUser={msg.role === 'USER'}
              >
                <S.MessageBubble $isUser={msg.role === 'USER'}>
                  {msg.role === 'BOT' ? formatBotMessage(msg.message) : msg.message}
                </S.MessageBubble>
              </S.MessageWrapper>
            ))}

            {isSending && (
              <S.MessageWrapper ref={lastMessageRef} $isUser={false}>
                <S.MessageBubble $isUser={false}>Thinking...</S.MessageBubble>
              </S.MessageWrapper>
            )}
          </>
        )}
      </S.MessagesScrollContainer>

      {/* Input Section */}
      <S.InputSection>
        {isAtCharacterLimit && (
          <S.LimitWarning>
            Character limit reached ({CHARACTER_LIMIT}/{CHARACTER_LIMIT})
          </S.LimitWarning>
        )}
        <S.InputContainer>
          {/* Address Suggestions */}
          {showAddressSuggestions &&
            addressSuggestions &&
            currentQuestion?.inputType === 'INPUT-API' && (
              <S.AddressSuggestionsWrapper>
                <AddressSuggestionsList
                  suggestions={addressSuggestions}
                  onSelect={handleAddressSelect}
                  isLoading={isLoadingAddresses}
                />
              </S.AddressSuggestionsWrapper>
            )}

          <S.TextArea
            ref={inputRef}
            placeholder={getCurrentPlaceholder()}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            disabled={isSending || isListening}
            readOnly={isListening}
            maxLength={CHARACTER_LIMIT}
            rows={1}
          />

          {/* Voice Button */}
          <S.VoiceButton
            onClick={isListening ? handleStopListening : handleStartListening}
            $isListening={isListening}
            aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
          >
            {isListening ? <MicOff /> : <Mic />}
          </S.VoiceButton>

          <S.SendButton onClick={() => handleSendMessage()} disabled={isSending}>
            <ArrowRight />
          </S.SendButton>

          {/* Listening Indicator */}
          {isListening && <S.ListeningIndicator>🎤 Listening...</S.ListeningIndicator>}
        </S.InputContainer>

        {/* AI Disclaimer */}
        <S.HelperText>
          <span>The AI can make mistakes. Please verify important information.</span>
        </S.HelperText>
      </S.InputSection>

      {/* End Conversation Popup */}
      {showEndConversation && (
        <EndConversationPopup
          isOpen={showEndConversation}
          onClose={handleCancelEnd}
          onSave={handleSaveConversation}
          isSaving={isSavingConversation}
        />
      )}

      {/* Suggestions Popup */}
      {initialFlowComplete &&
        councilInfo?.questionList &&
        hasInputApiQuestion &&
        questionNumber === 4 &&
        !suggestionClick && (
          <SuggestionsPopup
            isOpen={
              initialFlowComplete &&
              councilInfo?.questionList &&
              questionNumber === 4 &&
              !suggestionClick
            }
            onClose={() => {
              setSuggestionClick(true);
              // Focus input after closing suggestions
              setTimeout(() => inputRef.current?.focus(), 100);
            }}
            suggestions={councilInfo?.questionList || []}
            onSuggestionClick={handleSuggestionClick}
          />
        )}
    </S.ContentContainer>
  );
};
